<template>
    <div class="bg-[color:var(--background)]">
        <!-- <h1 class="text-6xl">### {{ hero_index }} ###</h1> -->

        <Hero :title="heroTitle" :highlight-words="heroKeywords" :buttons="[{
            link: 'https://app.unified.to',
            title: 'Start free 30-day trial',
        },
        {
            link: 'https://calendly.com/d/cph9-g8n-jzg/connect-with-unified',
            title: 'Book a demo',
            icon: 'ic:round-keyboard-arrow-right',
        }]" :subtitle="heroSubtitle" :is-invisible="true" />

        <HeroImage image_url="/images/Hero-Graphic2.webp" :image_width="2282" :image_height="1246"
            image_srcset="/images/Hero-Graphic2-320w.webp 320w, /images/Hero-Graphic2-480w.webp 480w, /images/Hero-Graphic2-640w.webp 640w, /images/Hero-Graphic2-960w.webp 960w, /images/Hero-Graphic2-1280w.webp 1280w, /images/Hero-Graphic2-1600w.webp 1600w, /images/Hero-Graphic2.webp 2282w"
            image_sizes="calc(85vw - 40px)" />

        <section class="mt-20 mb-20">
            <Customers lazy />
        </section>

        <Modern />

        <Benefits label bottom_image lazy_images title="Deploy to production in days" :benefits="[
            {
                image_url: '/images/Step-1-Activate.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-1-Activate'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Activate integrations',
                body: `Enable integrations across ${Object.keys(MAP_CATS).length} API categories, from CRM to Accounting and Messaging.`,
            },
            {
                image_url: '/images/Step-2-Launch.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-2-Launch'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Launch integrations',
                body: 'Build, test, and launch multiple integrations simultaneously with only a few lines of code.',
            },
            {
                image_url: '/images/Step-3-Authorize.webp',
                image_width: 617,
                image_height: 800,
                image_srcset: STEP_IMAGE_SRCSET('Step-3-Authorize'),
                image_sizes: STEP_IMAGE_SIZES,
                title: 'Authorize integrations',
                body: auth_body,
            },
        ]" />

        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Unified API or Unified MCP?"
                subtitle="Go beyond iPass & ETLs and choose what's best for your application." :usecases="[
                    {

                        title: 'Unified API',
                        body: 'Does your application need access to your customers\' actual data? If you are building an AI app, are you running that data through an embedding model and then storing the result in a vector database?',
                        category: 'API',
                        button: { link: '/technology', title: 'Explore our technology', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Unified MCP',
                        body: 'Are you building an AI agent that needs to read and write data across multiple integrations? MCP provides unified write endpoints so your agent can trigger actions, update records, and handle business logic securely.',
                        category: 'MCP',
                        button: { link: '/mcp', title: 'Learn more about Unified MCP', icon: 'ic:round-keyboard-arrow-right', },
                    }
                ]" />
        </section>


        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Fresh data to fuel countless AI use cases"
                subtitle="Power AI copilots, agents, insights, predictive analysis, and automation with a modern integration infrastructure that scales alongside you."
                :usecases="[
                    {
                        title: 'Offer Assessment Packages',
                        body: 'Offer assessment packages and take orders from ATS.',
                        category: 'assessment',
                        button: { link: 'https://unified.to/assessment', title: 'Take the assessment', icon: 'ic:round-keyboard-arrow-right' },
                    },
                    {
                        // image_url: '/icons/magnifying_glass.svg',
                        title: 'Source Candidates',
                        body: 'Enable customers to source candidates and send updates back to their ATS.',
                        category: 'ATS',
                        button: { link: 'https://unified.to/ats', title: 'Explore ATS integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/dart.svg',
                        title: 'Generate Leads',
                        body: 'Automate capturing leads and keep CRM information up-to-date.',
                        category: 'CRM',
                        button: { link: 'https://unified.to/crm', title: 'Explore CRM integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/brain.svg',
                        title: 'Assess Candidates',
                        body: 'Trigger assessments and send updates back to your customers’ ATS.',
                        category: 'ATS',
                        button: { link: 'https://unified.to/ats', title: 'Explore ATS integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/alert.svg',
                        title: 'Automate Inventory',
                        body: 'Assess stock shortages and trigger inventory refills.',
                        category: 'E-COMMERCE',
                        button: { link: 'https://unified.to/kms', title: 'Explore KMS integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/user.svg',
                        title: 'Onboarding',
                        body: 'Onboard employees and update profiles when roles change.',
                        category: 'HR',
                        button: { link: 'https://unified.to/hris', title: 'Explore HRIS integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/math.svg',
                        title: 'Analyze Financials',
                        body: 'Pull financial data to support insights and ROI calculations.',
                        category: 'ACCOUNTING',
                        button: { link: 'https://unified.to/accounting', title: 'Explore Accounting integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/bag.svg',
                        title: 'Promote Jobs',
                        body: 'Enable users to multiple job boards and send updates to their ATS.',
                        category: 'ATS',
                        button: { link: 'https://unified.to/ats', title: 'Explore ATS integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/bell.svg',
                        title: 'Customizable notifications',
                        body: 'Get alerts on tasks and deadlines that matter most.',
                        category: 'TASKS',
                        button: { link: 'https://unified.to/crm', title: 'Explore CRM integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Automate legal billing',
                        body: 'Capture billable time and expenses against matters in law-firm practice management systems.',
                        category: 'LEGAL',
                        button: { link: 'https://unified.to/legal', title: 'Explore Legal integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Compare GPU clouds',
                        body: 'Show GPU prices and availability across providers and launch instances on the cheapest one.',
                        category: 'COMPUTE',
                        button: { link: 'https://unified.to/compute', title: 'Explore Compute integrations', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Book patient appointments',
                        body: 'Show open slots and book appointments directly in clinic and health system EHRs.',
                        category: 'HEALTHCARE',
                        button: { link: 'https://unified.to/healthcare', title: 'Explore Healthcare integrations', icon: 'ic:round-keyboard-arrow-right', },
                    }
                ]" />
        </section>

        <IntegrationsHero />

        <MarginImageComponent title="AI data access infrastructure" image_url="/images/Data-for-AI.svg"
            subtitle="Unified.to meticulously cleans and structures your customer data, enhancing data flows and enabling real-time processing for AI applications."
            :data="[
                'Eliminate data latency with real-time access to customer data',
                'Harness our unified data schema to boost model accuracy and drive smarter AI insights',
                `Build for your use cases today and tomorrow with ${INTEGRATIONS.length} integrations and ${Object.keys(MAP_CATS).length} API categories`,
                'Effortlessly access all of your customer\'s data to feed your RAG pipeline in real-time',
                'Unified MCP\'s write endpoints let your AI agents push updates back into CRMs, ATS, HR systems, and more'
            ]" :button="{ link: 'https://app.unified.to', title: 'Get started free' }" />

        <QuadLayout title="The AI-first unified API platform"
            subtitle="Our declarative architecture is based on three decades of API expertise. Experience 10x launch speeds, real-time data, and zero maintenance."
            :platformfeatures="[
                {
                    image_url: '/images/SDKs.svg',
                    title: 'Modern SDKs',
                    body: 'Unified.to works with 7+ languages  and auto-updates SDK versions.',
                    rectangular: false,
                },
                {
                    image_url: '/images/Complete-Unification.svg',
                    title: 'Complete unification',
                    body: `We've unified everything: API endpoints, data models, authorization, webhooks and permission scopes for seamless builds and launches.`,
                    rectangular: true,
                },
                {
                    image_url: '/images/Real-time-Data.svg',
                    title: 'Native webhooks',
                    body: 'Never stored or cached — Unified.to fetches data  from the source in real-time, ensuring the most accurate data for time-sensitive features. ',
                    rectangular: true,
                },
                {
                    image_url: '/images/Virtual-Webhooks.svg',
                    title: 'Virtual webhooks',
                    body: `We offer a single consistent webhook API with a sophisticated scheduling architecture for integrations that don't support them natively, giving you greater control over API requests and spend.`,
                    rectangular: false,
                },
            ]" />

        <Benefits bottom_image title="Front-end components for you and your users" :benefits="[
            {
                image_url: '/images/Embedded-Authorization.svg',
                image_width: 713,
                image_height: 572,
                title: 'Authorization',
                body: authorization_body,
            },
            {
                image_url: '/images/Embedded-Sign-up.svg',
                image_width: 772,
                image_height: 908,
                title: 'Single Sign-On',
                body: 'Add user logins and signups with OAuth 2 OpenID and 60+ identity providers.',
            },
            {
                image_url: '/images/Embedded-Directory.svg',
                image_width: 772,
                image_height: 908,
                title: 'Directory',
                body: 'Add active integrations to a web or app page and update it without code changes.',
            },
        ]" />

        <Testimonials />

        <Hero title="Integrate. Faster." heading-level="h2" colour-index="1" bg :buttons="[{
            link: 'https://app.unified.to',
            title: 'Get started free',
        },
        {
            link: '/contact',
            title: 'Speak to an expert',
            icon: 'ic:round-keyboard-arrow-right',
        }]" />
    </div>
</template>

<script lang="ts">
import { CAT_SECTIONS } from '~~/data/sections';
import { INTEGRATIONS, INTEGRATIONS_COUNT_10s } from '~~/services/integrations';
import { MAP_CATS } from '~~/services/categories';

// Responsive variants for the three "Deploy to production" step images (see
// TICKET-10191). The rendered slot is 256px at >=640px (capped by the
// sm:max-w-xs card minus its p-8 padding) and ~100vw-64px below that, so
// mobile — not desktop — determines the largest candidate needed.
const STEP_IMAGE_WIDTHS = [254, 508, 644, 966];
function STEP_IMAGE_SRCSET(base: string): string {
    return STEP_IMAGE_WIDTHS.map((w) => `/images/${base}-${w}w.webp ${w}w`).join(', ');
}
const STEP_IMAGE_SIZES = '(min-width: 640px) 256px, calc(100vw - 64px)';

export default {
    props: {
        dark: Boolean,
    },
    setup() {
        // Ensure deterministic SSR/CSR hydration by storing the random choice in Nuxt state
        const isVariantA = useState('home-hero-variant', () => Math.random() > 0.5);

        const heroTitle = isVariantA.value
            ? 'Real-time Data Infrastructure'
            // 'The most advanced real-time unified API' : 
            : 'Secure real-time unified API+MCP';

        const heroKeywords = isVariantA.value
            ? ['Real-time']
            : ['unified', 'API+MCP'];

        const heroSubtitle = `Unlock your customers' data from ${INTEGRATIONS.length} sources across ${Object.keys(MAP_CATS).length} categories — built for B2B SaaS applications and AI agents using a real-time unified API + MCP.`;

        useSEO({
            title: 'Unified — Real-time Unified API and MCP Platform',
            noSuffix: true,
            description:
                'Developers who use our Unified API launch integrations in hours instead of months. One API and data model to add, update, and monitor HR, sales, marketing, support, ticketing and accounting integrations. Starting with a free account.',
        });

        // Point LLMs and AI crawlers arriving on the home page to the site's
        // Markdown resources: llms.txt indexes every page that has a clean
        // Markdown (`.md`) version, and llms-full.txt is the full-text corpus.
        // The home page's own `.md` sibling (/index.md, generated from the
        // integrations dataset) is advertised separately via PageActions.
        useHead({
            link: [
                { rel: 'alternate', type: 'text/markdown', href: 'https://unified.to/llms.txt', title: 'llms.txt' },
                { rel: 'alternate', type: 'text/markdown', href: 'https://unified.to/llms-full.txt', title: 'llms-full.txt' },
            ],
        });

        return {
            MAP_CATS,
            INTEGRATIONS,
            heroKeywords,
            heroTitle,
            heroSubtitle,
            INTEGRATIONS_COUNT_10s,
            STEP_IMAGE_SRCSET,
            STEP_IMAGE_SIZES,
        }
    },
    data() {
        return {
            authorization_body: 'Allow users to easily link their accounts with a single authorization flow. <br/><a href="/embeddedauth" class="text-blue-500 underline">Learn more about Embedded Auth</a>.',
            auth_body: 'Embed our authorization component to let customers easily link their accounts. <br/><a href="/embeddedauth" class="text-blue-500 underline">Learn more about Embedded Auth</a>.',
            RESULTS_EMPLOYEES: [
                {
                    id: '5eb3081053162e8b59189272',
                    created_at: '2023-06-14T16:44:22.170Z',
                    updated_at: '2023-06-14T16:44:22.170Z',
                    name: 'string',
                    emails: ['mary@foobar.com'],
                    department: 'string',
                    division: 'string',
                    title: 'string',
                    manager_id: '5eb3081053162e8b59189272',
                    employment_status: 'active',
                    location: 'string',
                    gender: 'male',
                    telephones: ['+1-555-123-4567'],
                    date_of_birth: '2023-06-14T16:44:22.170Z',
                    employee_number: 'string',
                    hired_at: '2023-06-14T16:44:22.170Z',
                    terminated_at: '2023-06-14T16:44:22.170Z',
                    address: {
                        address1: 'string',
                        address2: 'string',
                        city: 'string',
                        region: 'string',
                        region_code: 'string',
                        postal_code: 'string',
                        country: 'string',
                        country_code: 'string',
                    },
                    marital_status: 'single',
                    employment_type: 'SEASONAL',
                    raw: {},
                },
            ],
            GET_EMPLOYEES: {
                method: 'get',
                path: '/hris/{connection_id}/employee',
                summary: 'List all Employees',
                parameters: [],
                consumes: ['application/json'],
                produces: ['application/json'],
                // tags: ['hris', 'employee'],
                responses: {
                    '200': {
                        schema: {
                            $ref: '#/definitions/HrisEmployees',
                        },
                        description: 'Successful',
                    },
                },
                security: [
                    {
                        jwt: [],
                    },
                ],
                operationId: 'getHrisConnection_idEmployee',
            },

            // lastRandomSlot: -1,
            SECTIONS: CAT_SECTIONS,
        };
    },
    methods: {},
    mounted() {
        // console.log('mounted', this.hero_index);
        // this.HERO = HEROS[this.hero_index];
    },
    // watch: {
    //     hero_index() {
    //         console.log('hero index changed', this.hero_index);
    //     },
    // },
};
</script>